import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Clock3, PencilLine, Plus, Timer, Trash2, X } from 'lucide-react';
import { api } from '../../api';
import {
  canCreateWorklog,
  canDeleteWorklog,
  canEditWorklog,
  defaultWorkCategory,
  type WorkCategory,
  type WorklogPayload,
  type WorklogRow,
  type WorklogsPanelProps,
} from './worklog-model';
import './worklogs.css';

function minutesLabel(value: number) {
  const safe = Number.isFinite(value) ? Math.max(0, value) : 0;
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  if (!hours) return `${minutes} мин`;
  return minutes ? `${hours} ч ${minutes} мин` : `${hours} ч`;
}

function dateTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Дата не указана'
    : new Intl.DateTimeFormat('ru-RU', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function initials(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || '—';
}

type WorklogEditorProps = {
  row: WorklogRow;
  categories: WorkCategory[];
  ticketId: string;
  onCancel: () => void;
  onSaved: () => void;
};

function WorklogEditor({ row, categories, ticketId, onCancel, onSaved }: WorklogEditorProps) {
  const [description, setDescription] = useState(row.description || '');
  const [categoryId, setCategoryId] = useState(row.work_category_id || defaultWorkCategory(categories));
  const [isBillable, setIsBillable] = useState(row.is_billable);
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: () => api(`/tickets/${ticketId}/worklogs/${row.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        description: description.trim(),
        workCategoryId: categoryId,
        isBillable,
      }),
    }),
    onSuccess: onSaved,
    onError: reason => setError(errorMessage(reason, 'Не удалось изменить трудозатрату')),
  });

  return (
    <form className="worklog-editor" onSubmit={event => { event.preventDefault(); setError(''); mutation.mutate(); }}>
      <label>
        <span>Категория работ</span>
        <select value={categoryId} onChange={event => setCategoryId(event.target.value)} required>
          {categories.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
        </select>
      </label>
      <label className="worklog-editor-description">
        <span>Описание</span>
        <textarea value={description} onChange={event => setDescription(event.target.value)} rows={2} maxLength={1000} />
      </label>
      <label className="worklog-checkbox">
        <input type="checkbox" checked={isBillable} onChange={event => setIsBillable(event.target.checked)} />
        <span>Оплачиваемая работа</span>
      </label>
      {error && <p className="worklogs-error" role="alert">{error}</p>}
      <div className="worklog-editor-actions">
        <button type="button" className="worklogs-button secondary" onClick={onCancel}><X /> Отмена</button>
        <button type="submit" className="worklogs-button primary" disabled={!categoryId || mutation.isPending}><Check /> {mutation.isPending ? 'Сохранение…' : 'Сохранить'}</button>
      </div>
    </form>
  );
}

export function WorklogsPanel({ ticketId, ticketCategoryId, role, currentUserId, ticketStatus, onChanged }: WorklogsPanelProps) {
  const queryClient = useQueryClient();
  const [minutes, setMinutes] = useState('30');
  const [description, setDescription] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [isBillable, setIsBillable] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const categoriesQuery = useQuery({
    queryKey: ['work-categories'],
    queryFn: () => api<WorkCategory[]>('/work-categories'),
  });
  const worklogsQuery = useQuery({
    queryKey: ['worklogs', ticketId],
    queryFn: () => api<WorklogPayload>(`/tickets/${ticketId}/worklogs`),
  });
  const activeCategories = useMemo(
    () => (categoriesQuery.data || []).filter(category => category.is_active),
    [categoriesQuery.data],
  );

  useEffect(() => {
    if (!activeCategories.length) return;
    setSelectedCategoryId(current => activeCategories.some(category => category.id === current)
      ? current
      : defaultWorkCategory(activeCategories, ticketCategoryId));
  }, [activeCategories, ticketCategoryId]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['worklogs', ticketId] }),
      queryClient.invalidateQueries({ queryKey: ['timeline', ticketId] }),
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] }),
      queryClient.invalidateQueries({ queryKey: ['billing'] }),
      queryClient.invalidateQueries({ queryKey: ['billing-summary'] }),
    ]);
    onChanged?.();
  };

  const createMutation = useMutation({
    mutationFn: () => api(`/tickets/${ticketId}/worklogs`, {
      method: 'POST',
      body: JSON.stringify({
        durationMinutes: Number(minutes),
        description: description.trim(),
        isBillable,
        workCategoryId: selectedCategoryId,
      }),
    }),
    onSuccess: async () => {
      setMinutes('30');
      setDescription('');
      setIsBillable(true);
      setError('');
      await refresh();
    },
    onError: reason => setError(errorMessage(reason, 'Не удалось добавить трудозатрату')),
  });

  const deleteMutation = useMutation({
    mutationFn: (row: WorklogRow) => api(`/tickets/${ticketId}/worklogs/${row.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setError('');
      await refresh();
    },
    onError: reason => setError(errorMessage(reason, 'Не удалось удалить трудозатрату')),
  });

  const categoriesError = categoriesQuery.error
    ? errorMessage(categoriesQuery.error, 'Не удалось загрузить категории работ')
    : '';
  const worklogsError = worklogsQuery.error
    ? errorMessage(worklogsQuery.error, 'Не удалось загрузить трудозатраты')
    : '';
  const payload = worklogsQuery.data;
  const canCreate = canCreateWorklog(payload, role);
  const effectiveTicketStatus = payload?.ticket_status ?? ticketStatus;
  const submitDisabled = createMutation.isPending
    || !selectedCategoryId
    || !Number.isInteger(Number(minutes))
    || Number(minutes) < 1
    || Number(minutes) > 1440;

  return (
    <div className="worklogs-panel">
      <div className="worklogs-heading">
        <div>
          <span className="worklogs-kicker">УЧЁТ ВРЕМЕНИ</span>
          <h2>Трудозатраты</h2>
          <p>Фактические работы по заявке, категории и оплачиваемости.</p>
        </div>
        {worklogsQuery.isFetching && <span className="worklogs-sync">Обновление…</span>}
      </div>

      <div className="worklogs-summary" aria-label="Сводка трудозатрат">
        <div><span>Фактически</span><b>{minutesLabel(payload?.summary.total_minutes ?? 0)}</b></div>
        <div><span>Оплачиваемое время</span><b>{minutesLabel(payload?.summary.billable_minutes ?? 0)}</b></div>
      </div>

      {effectiveTicketStatus === 'CLOSED' && !canCreate && (
        <div className="worklogs-locked"><Clock3 /><span><b>Заявка закрыта</b><small>Добавление и изменение трудозатрат доступно только руководителю или администратору.</small></span></div>
      )}

      {canCreate && (
        <form className="worklogs-create" onSubmit={event => { event.preventDefault(); setError(''); createMutation.mutate(); }}>
          <label>
            <span>Продолжительность</span>
            <span className="worklogs-input-with-suffix">
              <input
                type="number"
                min="1"
                max="1440"
                step="1"
                value={minutes}
                onChange={event => setMinutes(event.target.value)}
                aria-label="Продолжительность работы в минутах"
                required
              />
              <em>мин</em>
            </span>
          </label>
          <label>
            <span>Категория работ</span>
            <select
              value={selectedCategoryId}
              onChange={event => setSelectedCategoryId(event.target.value)}
              aria-label="Категория выполненной работы"
              required
            >
              {!activeCategories.length && <option value="">Категории недоступны</option>}
              {activeCategories.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
            </select>
          </label>
          <label className="worklogs-description-field">
            <span>Описание</span>
            <textarea
              value={description}
              onChange={event => setDescription(event.target.value)}
              aria-label="Описание выполненной работы"
              placeholder="Что было выполнено"
              rows={2}
              maxLength={1000}
            />
          </label>
          <label className="worklog-checkbox worklogs-billable-field">
            <input type="checkbox" checked={isBillable} onChange={event => setIsBillable(event.target.checked)} />
            <span>Оплачиваемая работа</span>
          </label>
          <button type="submit" className="worklogs-button primary worklogs-submit" disabled={submitDisabled} aria-label="Добавить трудозатрату">
            <Plus /> {createMutation.isPending ? 'Добавление…' : 'Добавить трудозатрату'}
          </button>
        </form>
      )}

      {(error || categoriesError || worklogsError) && <p className="worklogs-error" role="alert">{error || categoriesError || worklogsError}</p>}

      <div className="worklogs-list">
        {(payload?.rows || []).map(row => {
          const editable = canEditWorklog(row, role, currentUserId);
          const deletable = canDeleteWorklog(row, role, currentUserId);
          return (
            <article key={row.id} className="worklog-row">
              <div className="worklog-row-main">
                <span className="worklog-avatar" aria-hidden="true">{initials(row.user_name)}</span>
                <div className="worklog-copy">
                  <header>
                    <b>{row.user_name}</b>
                    <time dateTime={row.worked_at}>{dateTime(row.worked_at)}</time>
                  </header>
                  <p>{row.description || 'Без описания'}</p>
                  <div className="worklog-tags">
                    <span className="category">{row.category_name || 'Категория не указана'}</span>
                    <span>{row.is_billable ? 'Оплачиваемая' : 'Неоплачиваемая'}</span>
                  </div>
                </div>
                <strong className="worklog-duration"><Clock3 /> {row.duration_minutes} мин</strong>
                {(editable || deletable) && (
                  <div className="worklog-row-actions">
                    {editable && <button type="button" onClick={() => setEditingId(current => current === row.id ? null : row.id)} aria-label={`Изменить трудозатрату ${row.user_name}`}><PencilLine /></button>}
                    {deletable && <button type="button" className="danger" disabled={deleteMutation.isPending && deleteMutation.variables?.id === row.id} onClick={() => deleteMutation.mutate(row)} aria-label={`Удалить трудозатрату ${row.user_name}`}><Trash2 /></button>}
                  </div>
                )}
              </div>
              {editingId === row.id && editable && (
                <WorklogEditor
                  row={row}
                  categories={activeCategories}
                  ticketId={ticketId}
                  onCancel={() => setEditingId(null)}
                  onSaved={async () => { setEditingId(null); await refresh(); }}
                />
              )}
            </article>
          );
        })}
      </div>

      {!worklogsQuery.isLoading && !payload?.rows.length && (
        <div className="worklogs-empty"><Timer /><b>Трудозатрат пока нет</b><span>{canCreate ? 'Добавьте первую выполненную работу.' : 'Записи появятся после фиксации выполненных работ.'}</span></div>
      )}
      {worklogsQuery.isLoading && <div className="worklogs-loading">Загружаем трудозатраты…</div>}
    </div>
  );
}
